import { queryOptions } from '@tanstack/react-query';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { EQueryKey } from '../queryKeys';
import { inspectionDB } from '../../data/database.structure';
import { EMutationKey } from '../mutationKeys';
import { ResponseError } from '../../api-contracts';
import { inspectionService } from '../../services/inspection/inspection.service';
import { inspectionRecordService } from '../../services/inspection/inspectionRecordService';
import InspectionProgressMessage from '../loadingInspectionProgressMessage';
import { describeSyncFailure } from '../../helpers/syncResultHelpers';

export const deleteLocalInspectionDBAtom = atomWithMutation(() => ({
  mutationKey: [EMutationKey.deleteInpsectionsLocalInspectionDB],
  mutationFn: async () => {
    await inspectionRecordService.clear();
    await inspectionDB.assets.clear();
    // TODO: clear all tables

    inspectionService.clearSyncState();
  },
  onError: () => {
    // handle error here
  },
}));

export const isPermissionDeniedError = (error: unknown): boolean =>
  error instanceof Error && error.message === 'PERMISSION_DENIED';

/** Retries after a failed sync attempt, before the inspection query reports the error. */
export const INSPECTION_SYNC_RETRIES = 3;

/** Shared by the startup query and "Refresh data", so both run, dedupe and retry the same way. */
export const inspectionQueryOptions = queryOptions({
  queryKey: [EQueryKey.inspectionQuery],
  queryFn: async () => {
    InspectionProgressMessage.set({
      isLoadingProgress: localStorage.getItem('lastSyncDate') === null,
    });

    try {
      return await inspectionService.syncInspectionData();
    } catch (error) {
      // Home shows this banner whenever it keeps its data through a failed sync;
      // the next sync clears it when it starts.
      InspectionProgressMessage.set({
        isLoadingProgress: false,
        msg: null,
        backgroundSyncError: describeSyncFailure(
          inspectionService.getLastSyncDate(),
        ),
      });
      if (error instanceof ResponseError && error.response.status === 403) {
        throw new Error('PERMISSION_DENIED');
      }
      throw error;
    }
  },
  networkMode: 'always',
  retry: (count: number, err: unknown) =>
    !isPermissionDeniedError(err) && count < INSPECTION_SYNC_RETRIES,
});

export const inspectionAtom = atomWithQuery(() => inspectionQueryOptions);
